// Author: Ahsan Saleem
/**
 * Chart primitives — the reusable, interactive time-series surface BloomOS was
 * missing. `SeriesChart` draws an average line (with a daily min/max envelope),
 * the site's optimal/alert band, day/night shading and a hover crosshair with a
 * readout; `Sparkline` is its axis-less miniature for tables and tiles. Both are
 * hand-rolled SVG (no charting dependency) and theme-aware through CSS tokens.
 */
import { useEffect, useRef, useState, type RefObject, type MouseEvent } from 'react';
import type { Pt, Band } from '../analytics.js';

/* Measure a container's width so the SVG is drawn at real pixel size (crisp, no
 * aspect distortion) and re-drawn on resize. */
function useMeasure(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((es) => { for (const e of es) setW(e.contentRect.width); });
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Nice, human round axis ticks across [lo,hi]. */
export function niceTicks(lo: number, hi: number, n: number): number[] {
  const span = hi - lo || 1;
  let step = Math.pow(10, Math.floor(Math.log10(span / n)));
  const err = (n * step) / span;
  if (err <= 0.15) step *= 10; else if (err <= 0.35) step *= 5; else if (err <= 0.75) step *= 2;
  const out: number[] = [];
  for (let s = Math.ceil(lo / step) * step; s <= hi + 1e-9; s += step) out.push(Number(s.toFixed(6)));
  return out;
}

/** Catmull-Rom → cubic-Bézier smoothing for a readable line. */
function smoothPath(P: Array<{ x: number; y: number }>): string {
  if (!P.length) return '';
  if (P.length < 3) return P.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  let d = `M${P[0].x.toFixed(1)},${P[0].y.toFixed(1)}`;
  for (let i = 0; i < P.length - 1; i++) {
    const p0 = P[i - 1] ?? P[i], p1 = P[i], p2 = P[i + 1], p3 = P[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}

function fmtAxisTime(t: number, rangeDays: number): string {
  const d = new Date(t);
  if (rangeDays <= 1) return `${d.getHours()}:00`;
  if (rangeDays <= 7) return DAYS[d.getDay()];
  if (rangeDays <= 90) return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return MONTHS[d.getMonth()];
}

const nf = (v: number, dec: number): string => v.toFixed(dec);

/* ── Sparkline ───────────────────────────────────────────────────────────── */
export function Sparkline({ points, band, colorVar = '--accent', height = 34 }: {
  points: Pt[]; band?: Band | null; colorVar?: string; height?: number;
}) {
  if (points.length < 2) return <span className="spark-empty" aria-hidden>—</span>;
  const W = 100, H = height, pad = 3;
  const vs = points.map((p) => p.v);
  let lo = Math.min(...vs), hi = Math.max(...vs);
  if (band) { lo = Math.min(lo, band.lo); hi = Math.max(hi, band.hi); }
  const span = hi - lo || 1;
  const X = (i: number) => (i / (points.length - 1)) * W;
  const Y = (v: number) => pad + (1 - (v - lo) / span) * (H - 2 * pad);
  const P = points.map((p, i) => ({ x: X(i), y: Y(p.v) }));
  const last = P[P.length - 1];
  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} height={H} preserveAspectRatio="none" role="img" aria-label="trend" style={{ color: `var(${colorVar})` }}>
      {band && <rect x={0} y={Math.max(0, Y(band.hi))} width={W} height={Math.max(0, Y(band.lo) - Y(band.hi))} className="spark-band" />}
      <path d={smoothPath(P)} fill="none" stroke="currentColor" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      <circle cx={last.x} cy={last.y} r={2.1} fill="currentColor" />
    </svg>
  );
}

/* ── SeriesChart ─────────────────────────────────────────────────────────── */
export function SeriesChart({ points, band, unit, decimals, mode, rangeDays, colorVar = '--accent', showNight = false, height = 300 }: {
  points: Pt[]; band: Band | null; unit: string; decimals: number;
  mode: 'raw' | 'daily'; rangeDays: number; colorVar?: string; showNight?: boolean; height?: number;
}) {
  const [ref, measured] = useMeasure();
  const [hover, setHover] = useState<number | null>(null);
  const W = measured || 680, H = height;
  const padL = 46, padR = 14, padT = 14, padB = 26;

  if (!points.length) {
    return <div className="chart-host" ref={ref} style={{ height: H }} />;
  }

  const t0 = points[0].t, t1 = points[points.length - 1].t;
  const dlo = mode === 'daily' ? Math.min(...points.map((p) => p.min ?? p.v)) : Math.min(...points.map((p) => p.v));
  const dhi = mode === 'daily' ? Math.max(...points.map((p) => p.max ?? p.v)) : Math.max(...points.map((p) => p.v));
  let lo = band ? Math.min(dlo, band.lo) : dlo;
  let hi = band ? Math.max(dhi, band.hi) : dhi;
  const pad = (hi - lo) * 0.08 || 1; lo -= pad; hi += pad;

  const X = (t: number) => padL + ((t - t0) / (t1 - t0 || 1)) * (W - padL - padR);
  const Y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);

  const linePts = points.map((p) => ({ x: X(p.t), y: Y(p.v) }));
  const ticksY = niceTicks(lo, hi, 4);
  const step = Math.max(1, Math.ceil(points.length / 6));
  const ticksX = points.filter((_, i) => i % step === 0);

  // day/night shading (lights off 23:00–05:00), intraday only
  const nights: Array<{ x: number; w: number }> = [];
  if (showNight) {
    for (let d = Math.floor(t0 / 86_400_000) * 86_400_000; d <= t1; d += 86_400_000) {
      const on = new Date(d); on.setHours(23, 0, 0, 0);
      const off = new Date(d + 86_400_000); off.setHours(5, 0, 0, 0);
      const a = Math.max(t0, on.getTime()), b = Math.min(t1, off.getTime());
      if (b > a) nights.push({ x: X(a), w: X(b) - X(a) });
    }
  }

  let envelope = '';
  if (mode === 'daily') {
    const top = points.map((p) => `${X(p.t).toFixed(1)},${Y(p.max ?? p.v).toFixed(1)}`);
    const bot = points.slice().reverse().map((p) => `${X(p.t).toFixed(1)},${Y(p.min ?? p.v).toFixed(1)}`);
    envelope = `M${top.join(' L')} L${bot.join(' L')} Z`;
  }

  const hp = hover != null ? points[hover] : null;
  const hx = hp ? X(hp.t) : 0, hy = hp ? Y(hp.v) : 0;

  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best = Infinity, bi = 0;
    for (let i = 0; i < points.length; i++) { const dx = Math.abs(X(points[i].t) - px); if (dx < best) { best = dx; bi = i; } }
    setHover(bi);
  };
  const fmtHoverDate = (t: number) => {
    const d = new Date(t);
    return mode === 'daily'
      ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
      : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chart-host" ref={ref} style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ maxWidth: '100%', color: `var(${colorVar})` }}
        role="img" aria-label="datapoint trend" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {nights.map((n, i) => <rect key={i} x={n.x} y={padT} width={n.w} height={H - padT - padB} className="ch-night" />)}
        {band && (
          <g>
            <rect x={padL} y={Y(band.hi)} width={W - padL - padR} height={Math.max(0, Y(band.lo) - Y(band.hi))} className="ch-band" />
            <line x1={padL} x2={W - padR} y1={Y(band.hi)} y2={Y(band.hi)} className="ch-band-edge" />
            <line x1={padL} x2={W - padR} y1={Y(band.lo)} y2={Y(band.lo)} className="ch-band-edge" />
          </g>
        )}
        {ticksY.map((v) => (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={Y(v)} y2={Y(v)} className="ch-grid" />
            <text x={padL - 6} y={Y(v) + 3} textAnchor="end" className="ch-axis">{nf(v, decimals === 0 ? 0 : 1)}</text>
          </g>
        ))}
        {ticksX.map((p, i) => (
          <text key={i} x={X(p.t)} y={H - 8} textAnchor="middle" className="ch-axis">{fmtAxisTime(p.t, rangeDays)}</text>
        ))}
        {mode === 'daily' && <path d={envelope} fill="currentColor" opacity={0.15} />}
        <path d={smoothPath(linePts)} fill="none" stroke="currentColor" strokeWidth={2} vectorEffect="non-scaling-stroke" />
        <circle cx={linePts[linePts.length - 1].x} cy={linePts[linePts.length - 1].y} r={3.2} fill="currentColor" />
        {hp && (
          <g>
            <line x1={hx} x2={hx} y1={padT} y2={H - padB} className="ch-cross" />
            <circle cx={hx} cy={hy} r={4} fill="currentColor" stroke="var(--canvas)" strokeWidth={1.5} />
          </g>
        )}
      </svg>
      {hp && (
        <div className="ch-tip" style={{ left: `${(hx / W) * 100}%`, top: `${(hy / H) * 100}%` }}>
          <div className="ch-tip-d">{fmtHoverDate(hp.t)}</div>
          <div className="ch-tip-r"><span>{mode === 'daily' ? 'avg' : 'reading'}</span><b>{nf(hp.v, decimals)} {unit}</b></div>
          {mode === 'daily' && hp.min != null && hp.max != null && (
            <div className="ch-tip-r"><span>min–max</span><b>{nf(hp.min, decimals)}–{nf(hp.max, decimals)}</b></div>
          )}
          {band && (
            <div className={`ch-tip-flag ${hp.v >= band.lo && hp.v <= band.hi ? 'ok' : hp.v > band.hi ? 'hi' : 'lo'}`}>
              {hp.v >= band.lo && hp.v <= band.hi ? '● within band' : hp.v > band.hi ? '▲ above band' : '▼ below band'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── DiurnalChart ────────────────────────────────────────────────────────── */
export function DiurnalChart({ hours, band, decimals, colorVar = '--accent', height = 180 }: {
  hours: Array<{ hour: number; avg: number; min: number; max: number }>; band: Band | null; decimals: number; colorVar?: string; height?: number;
}) {
  const [ref, measured] = useMeasure();
  const W = measured || 380, H = height, padL = 40, padR = 10, padT = 12, padB = 22;
  if (hours.length < 2) return <div className="chart-host" ref={ref} style={{ height: H }} />;
  let lo = Math.min(...hours.map((h) => h.min)), hi = Math.max(...hours.map((h) => h.max));
  if (band) { lo = Math.min(lo, band.lo); hi = Math.max(hi, band.hi); }
  const pad = (hi - lo) * 0.08 || 1; lo -= pad; hi += pad;
  const X = (h: number) => padL + (h / 23) * (W - padL - padR);
  const Y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
  const top = hours.map((h) => `${X(h.hour).toFixed(1)},${Y(h.max).toFixed(1)}`);
  const bot = hours.slice().reverse().map((h) => `${X(h.hour).toFixed(1)},${Y(h.min).toFixed(1)}`);
  return (
    <div className="chart-host" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ maxWidth: '100%', color: `var(${colorVar})` }} role="img" aria-label="diurnal profile">
        <rect x={X(0)} y={padT} width={X(5) - X(0)} height={H - padT - padB} className="ch-night" />
        <rect x={X(23)} y={padT} width={W - padR - X(23)} height={H - padT - padB} className="ch-night" />
        {band && <rect x={padL} y={Y(band.hi)} width={W - padL - padR} height={Math.max(0, Y(band.lo) - Y(band.hi))} className="ch-band" />}
        {niceTicks(lo, hi, 3).map((v) => (
          <g key={v}><line x1={padL} x2={W - padR} y1={Y(v)} y2={Y(v)} className="ch-grid" />
            <text x={padL - 5} y={Y(v) + 3} textAnchor="end" className="ch-axis">{nf(v, decimals === 0 ? 0 : 1)}</text></g>
        ))}
        {[0, 6, 12, 18, 23].map((h) => <text key={h} x={X(h)} y={H - 7} textAnchor="middle" className="ch-axis">{h}h</text>)}
        <path d={`M${top.join(' L')} L${bot.join(' L')} Z`} fill="currentColor" opacity={0.14} />
        <path d={smoothPath(hours.map((h) => ({ x: X(h.hour), y: Y(h.avg) })))} fill="none" stroke="currentColor" strokeWidth={2} vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

/* ── Histogram ───────────────────────────────────────────────────────────── */
export function Histogram({ bins, unit, decimals, colorVar = '--accent', height = 180 }: {
  bins: Array<{ lo: number; hi: number; count: number; inBand: boolean }>; unit: string; decimals: number; colorVar?: string; height?: number;
}) {
  const [ref, measured] = useMeasure();
  const W = measured || 340, H = height, padL = 10, padR = 10, padT = 12, padB = 24;
  if (!bins.length) return <div className="chart-host" ref={ref} style={{ height: H }} />;
  const mx = Math.max(...bins.map((b) => b.count)) || 1;
  const bw = (W - padL - padR) / bins.length;
  const lo = bins[0].lo, hi = bins[bins.length - 1].hi;
  return (
    <div className="chart-host" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ maxWidth: '100%', color: `var(${colorVar})` }} role="img" aria-label="distribution">
        {bins.map((b, i) => {
          const bh = (b.count / mx) * (H - padT - padB);
          return <rect key={i} x={padL + i * bw + 1} y={H - padB - bh} width={bw - 2} height={bh} rx={1.5}
            fill="currentColor" opacity={b.inBand ? 0.85 : 0.32} />;
        })}
        {[lo, (lo + hi) / 2, hi].map((v, i) => (
          <text key={i} x={i === 0 ? padL : i === 2 ? W - padR : W / 2} y={H - 7}
            textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'} className="ch-axis">{nf(v, decimals)}{i === 2 ? ` ${unit}` : ''}</text>
        ))}
      </svg>
    </div>
  );
}
